import { useQueries } from '@tanstack/react-query';

import { getCompanyById } from '../../companies/api/companies.client.js';
import { getContactById } from '../../contacts/api/contacts.client.js';
import { getDealById } from '../../deals/api/deals.client.js';
import { getDocumentById } from '../../documents/api/documents.client.js';
import { getProjectById } from '../../projects/api/projects.client.js';
import { getTaskById } from '../../tasks/api/tasks.client.js';
import { useScopeStore } from '../../../shared/scope/useCurrentScope.js';
import type { ApprovalSummary } from '../lib/approvalSummary.js';

type Target = NonNullable<ApprovalSummary['target']>;

export type ApprovalTargetLabelResolution =
  | { status: 'pending' }
  | { status: 'resolved'; label: string }
  | { status: 'failed' };

function targetKey(target: Pick<Target, 'kind' | 'id'>): string {
  return `${target.kind}:${target.id}`;
}

async function loadTargetLabel(target: Target): Promise<string | null> {
  switch (target.kind) {
    case 'deal':
      return (await getDealById(target.id)).title;
    case 'contact':
      return (await getContactById(target.id)).fullName;
    case 'company':
      return (await getCompanyById(target.id)).name;
    case 'task':
      return (await getTaskById(target.id)).title;
    case 'project':
      return (await getProjectById(target.id)).name;
    case 'document':
      return (await getDocumentById(target.id)).title;
    case 'meeting':
    case 'template':
      return null;
  }
}

/** Resolves each missing target label once per workspace and reuses React Query's detail cache. */
export function useApprovalTargetLabels(
  targets: readonly (Target | null)[],
): ReadonlyMap<string, ApprovalTargetLabelResolution> {
  const workspaceId = useScopeStore((state) => state.scope?.workspaceId ?? null);
  const unresolved = Array.from(
    new Map(
      targets
        .filter((target): target is Target => target !== null && target.label === undefined)
        .map((target) => [targetKey(target), target]),
    ).values(),
  );
  const results = useQueries({
    queries: unresolved.map((target) => ({
      queryKey: ['approval-target-label', workspaceId, target.kind, target.id] as const,
      queryFn: () => loadTargetLabel(target),
      enabled: workspaceId !== null,
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  return new Map(
    unresolved.map((target, index) => {
      const result = results[index];
      const label = result?.data;
      let resolution: ApprovalTargetLabelResolution;
      if (typeof label === 'string' && label.trim().length > 0) {
        resolution = { status: 'resolved', label };
      } else if (result?.isError || result?.isSuccess) {
        // A successful null result is an explicitly unsupported target kind.
        // It follows the same privacy-safe fallback as a failed lookup.
        resolution = { status: 'failed' };
      } else {
        resolution = { status: 'pending' };
      }
      return [targetKey(target), resolution] as const;
    }),
  );
}

export function approvalTargetKey(target: Pick<Target, 'kind' | 'id'>): string {
  return targetKey(target);
}
